/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type BotInfoForUpdate } from '@coze-arch/idl/playground_api';
import { fetchOntologyApps } from '@coze-common/ontology';
import { ontologyAppIdForPersistedMode } from '@coze-common/ontology/ontology-app-id';

import { type InferenceMode } from '@/store/inference-mode';

type ModelInfoUpdate = BotInfoForUpdate['model_info'];

/**
 * 本体模型 / 本体数仓落库前，把与当前模式不兼容的应用 id 写成空。
 * 没有 model_id 时后端会忽略整段 model_info，这里不动，避免写出无法覆盖的空对象。
 * 列表拉取失败时保留原 id。
 */
export async function modelInfoWithCompatibleOntologyApp(
  mode: InferenceMode,
  modelInfo: ModelInfoUpdate,
): Promise<ModelInfoUpdate> {
  if (mode !== 'ontology' && mode !== 'ontology_starrocks') {
    return modelInfo;
  }
  const current = modelInfo?.parameters?.ontology_app_id ?? '';
  if (!modelInfo?.model_id || !current.trim()) {
    return modelInfo;
  }

  let apps: Awaited<ReturnType<typeof fetchOntologyApps>> | null = null;
  try {
    apps = await fetchOntologyApps();
  } catch (error) {
    if (IS_DEV_MODE) {
      console.warn(
        '[inference-config] ontology app list failed, keep ontology_app_id',
        error,
      );
    }
    return modelInfo;
  }

  const nextId = ontologyAppIdForPersistedMode(mode, current, apps);
  if (nextId === current) {
    return modelInfo;
  }

  const parameters: Record<string, string> = {
    ...(modelInfo.parameters ?? {}),
    ontology_app_id: nextId,
  };
  if (!nextId) {
    delete parameters.ontology_app_name;
  }
  return { ...modelInfo, parameters };
}
